export type AdminUser = {
  id: number;
  email: string;
  name: string;
};

export type AdminSession = {
  token: string;
  user: AdminUser;
  expires_at: string;
};

export type AdminAccountUser = {
  id: number;
  email: string;
  name: string;
  is_active: number;
  created_at: string;
};

const STORAGE_KEYS = {
  token: 'cardapio_admin_token',
  user: 'cardapio_admin_user',
};

export const ADMIN_SESSION_EVENT = 'cardapio_admin_session_changed';

export function notifyAdminSessionChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(ADMIN_SESSION_EVENT));
  }
}

export function getApiBase() {
  if (typeof window === 'undefined') return '/api';
  const path = window.location.pathname;
  return path.includes('/Site_Cardapio') ? '/Site_Cardapio/api' : '/api';
}

export function getAdminToken() {
  return localStorage.getItem(STORAGE_KEYS.token) ?? '';
}

export function setAdminToken(token: string) {
  if (!token) {
    localStorage.removeItem(STORAGE_KEYS.token);
    notifyAdminSessionChanged();
    return;
  }

  localStorage.setItem(STORAGE_KEYS.token, token);
  notifyAdminSessionChanged();
}

export function getStoredAdminUser(): AdminUser | null {
  const raw = localStorage.getItem(STORAGE_KEYS.user);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as AdminUser;
  } catch {
    localStorage.removeItem(STORAGE_KEYS.user);
    return null;
  }
}

export function setStoredAdminUser(user: AdminUser | null) {
  if (!user) {
    localStorage.removeItem(STORAGE_KEYS.user);
    notifyAdminSessionChanged();
    return;
  }

  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
  notifyAdminSessionChanged();
}

export function clearAdminSession() {
  localStorage.removeItem(STORAGE_KEYS.token);
  localStorage.removeItem(STORAGE_KEYS.user);
  notifyAdminSessionChanged();
}

async function apiRequest<T>(path: string, options: RequestInit = {}, body?: unknown) {
  const headers = new Headers(options.headers || {});

  if (!(body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const token = getAdminToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${getApiBase()}${path}`, {
    ...options,
    headers,
    body: body !== undefined ? (body instanceof FormData ? body : JSON.stringify(body)) : options.body,
  });

  const text = await response.text();
  let payload: any = {};
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { message: response.ok ? 'Resposta inválida do servidor.' : `Erro ${response.status}: resposta inválida do servidor.` };
    }
  }

  if (!response.ok) {
    throw new Error(payload?.message || 'Erro na requisição');
  }

  return payload as T;
}

export async function loginAdmin(email: string, password: string) {
  return apiRequest<{ success: boolean; token: string; user: AdminUser; expires_at: string }>(
    '/auth.php',
    { method: 'POST' },
    { action: 'login', email, password }
  );
}

export async function changeAdminPassword(currentPassword: string, newPassword: string) {
  return apiRequest<{ success: boolean; message: string }>(
    '/auth.php',
    { method: 'POST' },
    { action: 'change_password', current_password: currentPassword, new_password: newPassword }
  );
}

export async function createAdminUser(name: string, email: string, password: string) {
  return apiRequest<{ success: boolean; message: string; user: AdminUser }>(
    '/auth.php',
    { method: 'POST' },
    { action: 'create_admin', name, email, password }
  );
}

export async function listAdminUsers() {
  return apiRequest<{ success: boolean; data: AdminAccountUser[] }>(
    '/auth.php',
    { method: 'POST' },
    { action: 'list_admin_users' }
  );
}

export async function resetAdminUserPassword(userId: number, newPassword: string) {
  return apiRequest<{ success: boolean; message: string }>(
    '/auth.php',
    { method: 'POST' },
    { action: 'reset_admin_password', user_id: userId, new_password: newPassword }
  );
}

export async function listTable<T = any>(table: string): Promise<T[]> {
  const response = await apiRequest<{ success: boolean; data: T[] }>(`/menu.php?table=${encodeURIComponent(table)}`);
  return response.data || [];
}

export async function insertTable(table: string, data: Record<string, unknown>) {
  return apiRequest<{ success: boolean; message: string; id?: number }>(
    '/menu.php',
    { method: 'POST' },
    { table, action: 'insert', data }
  );
}

export async function updateTable(table: string, data: Record<string, unknown>) {
  return apiRequest<{ success: boolean; message: string }>(
    '/menu.php',
    { method: 'POST' },
    { table, action: 'update', data }
  );
}

export async function deleteTable(table: string, id: number | string) {
  return apiRequest<{ success: boolean; message: string }>(
    '/menu.php',
    { method: 'POST' },
    { table, action: 'delete', id }
  );
}

export async function uploadImage(file: File, nameHint?: string) {
  const formData = new FormData();
  formData.append('image', file);
  if (nameHint?.trim()) {
    formData.append('name_hint', nameHint.trim());
  }

  const response = await apiRequest<{ success: boolean; url: string; message?: string }>(
    '/uploads.php',
    { method: 'POST' },
    formData
  );

  return response;
}
